/**
 * StyleGallery — the studio entry step: one card per pass type instead of a
 * dropdown. Generic over the value type so the Apple (AppleStyle) and Google
 * (GoogleStyle) studios share it. Pure presentational.
 */
export interface StyleCardOption<T extends string> {
  value: T;
  title: string;
  description: string;
  /** Optional corner badge, e.g. 'Élő' on the flight-tracking types. */
  badge?: string;
  /** Not pickable (e.g. a template the partner has no access to). */
  disabled?: boolean;
  /** Honest hint rendered on a disabled card, e.g. why it is unavailable. */
  disabledHint?: string;
}

export interface StyleGalleryProps<T extends string> {
  options: ReadonlyArray<StyleCardOption<T>>;
  onPick: (value: T) => void;
}

export function StyleGallery<T extends string>({
  options,
  onPick,
}: StyleGalleryProps<T>): JSX.Element {
  return (
    <div
      data-testid="style-gallery"
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          disabled={o.disabled}
          onClick={() => onPick(o.value)}
          className={`text-left rounded-xl p-4 ring-1 transition space-y-1 ${
            o.disabled
              ? 'bg-slate-50 ring-slate-100 opacity-60 cursor-not-allowed'
              : 'bg-white ring-slate-200 hover:ring-brand-500 hover:shadow-md'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="font-semibold text-slate-900">{o.title}</span>
            {o.badge ? (
              <span className="text-[10px] font-bold uppercase tracking-wider rounded-full bg-amber-100 text-amber-800 px-2 py-0.5">
                {o.badge}
              </span>
            ) : null}
          </div>
          <p className="text-xs text-slate-500">{o.description}</p>
          {o.disabled && o.disabledHint ? (
            <p className="text-xs text-amber-700">{o.disabledHint}</p>
          ) : null}
        </button>
      ))}
    </div>
  );
}
